'use client';

import React from 'react';
import { Heart } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';
import { useUser } from '@clerk/nextjs';
import { getOrCreateVisitorId } from '@/lib/visitor-id';

interface WishlistButtonProps {
  journeyId: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export default function WishlistButton({
  journeyId,
  size = 'md',
  showLabel = false,
  className = ''
}: WishlistButtonProps) {
  const { isSignedIn } = useUser();
  const utils = trpc.useUtils();

  // Get visitor ID for anonymous users
  const visitorId = !isSignedIn ? getOrCreateVisitorId() : undefined;

  // Check if journey is in wishlist
  const { data: wishlistStatus, isLoading } = trpc.wishlist.check.useQuery(
    { journeyId, visitorId },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const isInWishlist = wishlistStatus?.isInWishlist || false;

  // Add to wishlist mutation
  const addToWishlist = trpc.wishlist.add.useMutation({
    onSuccess: () => {
      console.log('[Wishlist] Successfully added journey', journeyId);
      utils.wishlist.check.invalidate({ journeyId, visitorId });
      utils.wishlist.getAll.invalidate({ visitorId });
      utils.wishlist.getCount.invalidate({ visitorId });
    },
    onError: (error) => {
      console.error('[Wishlist Error] Full error object:', error);
      console.error('[Wishlist Error] Error shape:', JSON.stringify(error.shape, null, 2));
      console.error('[Wishlist Error] Error data:', error.data);
      console.error('[Wishlist Error] Error message:', error.message);

      if (error.message.includes('CONFLICT') || error.message.includes('already')) {
        alert('This journey is already in your wishlist.');
      } else {
        alert(`Failed to add to wishlist: ${error.message}`);
      }
    },
  });

  // Remove from wishlist mutation
  const removeFromWishlist = trpc.wishlist.remove.useMutation({
    onSuccess: () => {
      utils.wishlist.check.invalidate({ journeyId, visitorId });
      utils.wishlist.getAll.invalidate({ visitorId });
      utils.wishlist.getCount.invalidate({ visitorId });
    },
    onError: (error) => {
      console.error('Error removing from wishlist:', error);

      if (error.message.includes('NOT_FOUND')) {
        alert('This journey is not in your wishlist.');
      } else {
        alert('Failed to remove from wishlist. Please try again.');
      }
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('[Wishlist Click] Journey ID:', journeyId);
    console.log('[Wishlist Click] Signed in:', isSignedIn, 'Visitor ID:', visitorId);
    console.log('[Wishlist Click] Is in wishlist:', isInWishlist);

    // Toggle wishlist status (works for both authenticated and anonymous users)
    if (isInWishlist) {
      console.log('[Wishlist] Removing from wishlist...');
      removeFromWishlist.mutate({ journeyId, visitorId });
    } else {
      console.log('[Wishlist] Adding to wishlist...');
      addToWishlist.mutate({ journeyId, visitorId });
    }
  };

  // Size configurations
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const isProcessing = addToWishlist.isPending || removeFromWishlist.isPending;
  const isButtonDisabled = isProcessing || isLoading;

  return (
    <button
      onClick={handleClick}
      disabled={isButtonDisabled}
      className={`
        ${sizeClasses[size]}
        flex 
        items-center 
        justify-center 
        group
        disabled:opacity-50
        disabled:cursor-not-allowed
        transition-all 
        duration-200
        ${className}
      `}
      aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <Heart
        className={`
          ${iconSizes[size]}
          transition-all 
          duration-200
          drop-shadow-lg
          ${isInWishlist 
            ? 'text-white group-hover:scale-110' 
            : 'text-white hover:fill-white/80 group-hover:scale-110'
          }
          ${isProcessing ? 'animate-pulse' : ''}
        `}
        fill={isInWishlist ? '#cdc50a' : 'none'}
        strokeWidth={2.5}
      />
      {showLabel && (
        <span className="ml-2 text-sm font-medium text-white drop-shadow-lg">
          {isInWishlist ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}

